import React, { useEffect } from 'react';
import { useClarityMode } from '@/contexts/ClarityContext';
import { Helmet } from 'react-helmet-async';

interface ClaritySnapshotModeProps {
  children: React.ReactNode;
}

const ClaritySnapshotMode: React.FC<ClaritySnapshotModeProps> = ({ children }) => {
  const { isClarityMode } = useClarityMode();

  useEffect(() => {
    if (!isClarityMode) return;

    // Function to hide all JivoChat elements
    const hideJivoChat = () => {
      const jivoSelectors = [
        '#jivo-iframe-container',
        '#__jivoMobileButton',
        '[id^="jivo"]',
        '[class*="jivo"]',
        'jdiv'
      ];
      
      jivoSelectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          (el as HTMLElement).style.display = 'none';
          (el as HTMLElement).style.visibility = 'hidden';
          (el as HTMLElement).style.opacity = '0';
        });
      });
    };

    // Initial hide
    hideJivoChat();

    // Disable all popups
    const popupElements = document.querySelectorAll('[data-popup], [data-modal], [data-dialog]');
    popupElements.forEach(el => {
      (el as HTMLElement).style.display = 'none';
    });

    // MutationObserver to catch dynamically added JivoChat elements
    const observer = new MutationObserver(() => {
      hideJivoChat();
    });

    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });

    // Disable scroll observers
    const scrollObservers = (window as any).__scrollObservers || [];
    scrollObservers.forEach((observer: IntersectionObserver) => observer.disconnect());

    // Force expand all collapsible sections
    const collapsibles = document.querySelectorAll('[data-state="closed"]');
    collapsibles.forEach(el => {
      el.setAttribute('data-state', 'open');
      (el as HTMLElement).style.height = 'auto';
    });

    // Remove animation classes
    const animatedElements = document.querySelectorAll('[class*="animate-"]');
    animatedElements.forEach(el => {
      const classes = Array.from(el.classList).filter(c => c.startsWith('animate-'));
      el.classList.remove(...classes);
    });

    // Set all images to eager loading
    const images = document.querySelectorAll('img[loading="lazy"]');
    images.forEach(img => {
      img.setAttribute('loading', 'eager');
    });

    console.log('[Clarity Snapshot Mode] Static rendering enabled, JivoChat hidden');

    return () => {
      observer.disconnect();
    };
  }, [isClarityMode]);

  if (!isClarityMode) {
    return <>{children}</>;
  }

  return (
    <>
      <Helmet>
        <meta name="clarity-snapshot" content="static" />
        <meta name="prerender-status-code" content="200" />
      </Helmet>
      <div className="clarity-static-wrapper">
        {children}
      </div>
    </>
  );
};

export default ClaritySnapshotMode;
